import React, { useState, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Link,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { getConfigSync } from '../services/config';

// Format time
function formatTime(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Generate ConnectWise ticket URL
const getTicketUrl = (ticketNumber) => {
  try {
    const config = getConfigSync();
    if (!config?.connectwiseUrl || !ticketNumber) return null;
    if (config.connectwiseCompanyName) {
      const baseUrl = config.connectwiseUrl.replace(/\/v4_6_release\/ConnectWise\.aspx.*$/, '');
      const redirectUrl = `services/system_io/Service/fv_sr100_request.rails?service_recid=${ticketNumber}&companyName=${config.connectwiseCompanyName}`;
      return `${baseUrl}/?redirecturl=${encodeURIComponent(redirectUrl)}`;
    }
    return `${config.connectwiseUrl}?routeTo=ServiceFXSR/${ticketNumber}`;
  } catch {
    return null;
  }
};

export default function BatchTicketReview() {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('with_suggestions');
  const [selectedCalls, setSelectedCalls] = useState(new Set());
  const [confirming, setConfirming] = useState({});
  const [stats, setStats] = useState({ total: 0, withSuggestions: 0, highConfidence: 0 });

  // Get API URL from config
  const getApiUrl = useCallback(() => {
    try {
      const config = getConfigSync();
      return config.apiUrl || 'https://recordings-api.adnetapps.com';
    } catch {
      return null;
    }
  }, []);

  // Get instance ID from config
  const getInstanceId = useCallback(() => {
    try {
      const config = getConfigSync();
      return config.instanceId;
    } catch {
      return null;
    }
  }, []);

  // Fetch batch suggestions
  const fetchBatchSuggestions = useCallback(async () => {
    const apiUrl = getApiUrl();
    const instanceId = getInstanceId();

    if (!apiUrl || !instanceId) {
      setError('Configuration not loaded');
      return;
    }

    setLoading(true);
    setError(null);
    setSelectedCalls(new Set());

    try {
      const response = await fetch(
        `${apiUrl}/api/auto-match/batch?instance_id=${instanceId}&call_date=${selectedDate}&min_confidence=0.6`,
        { method: 'POST' }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch suggestions: ${response.status}`);
      }

      const data = await response.json();
      setCalls(data.calls || []);
      setStats({
        total: data.total_untagged || 0,
        withSuggestions: data.with_suggestions || 0,
        highConfidence: data.high_confidence || 0,
      });
    } catch (err) {
      console.error('[BatchReview] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getApiUrl, getInstanceId, selectedDate]);

  // Confirm a single suggestion
  const handleConfirm = useCallback(async (call, suggestion) => {
    const apiUrl = getApiUrl();
    const instanceId = getInstanceId();

    if (!apiUrl || !instanceId) return;

    setConfirming((prev) => ({ ...prev, [call.call_id]: true }));

    try {
      const response = await fetch(`${apiUrl}/api/auto-match/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instance_id: instanceId,
          call_id: call.call_id,
          call_started_at: call.started_at,
          caller_number: call.caller_number,
          caller_name: call.caller_name,
          extension: call.extension,
          extension_name: call.extension_name,
          ticket_number: suggestion.ticket_number,
          ticket_summary: suggestion.ticket_summary,
          ticket_company: suggestion.company,
          confidence: suggestion.confidence,
          source: suggestion.source,
          tagged_by: 'Batch Review User',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to confirm');
      }

      // Remove the confirmed call from the list
      setCalls((prev) => prev.filter((c) => c.call_id !== call.call_id));
      setStats((prev) => ({
        ...prev,
        total: prev.total - 1,
        withSuggestions: prev.withSuggestions - 1,
        highConfidence: suggestion.confidence >= 0.85 ? prev.highConfidence - 1 : prev.highConfidence,
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setConfirming((prev) => ({ ...prev, [call.call_id]: false }));
    }
  }, [getApiUrl, getInstanceId]);

  // Skip a call (remove from list without tagging)
  const handleSkip = useCallback((callId) => {
    setCalls((prev) => prev.filter((c) => c.call_id !== callId));
    setSelectedCalls((prev) => {
      const next = new Set(prev);
      next.delete(callId);
      return next;
    });
  }, []);

  // Bulk confirm all selected high-confidence calls
  const handleBulkConfirm = useCallback(async () => {
    const callsToConfirm = calls.filter(
      (c) => selectedCalls.has(c.call_id) && c.suggestions?.length > 0
    );

    for (const call of callsToConfirm) {
      const suggestion = call.suggestions[0]; // Use top suggestion
      await handleConfirm(call, suggestion);
    }

    setSelectedCalls(new Set());
  }, [calls, selectedCalls, handleConfirm]);

  // Toggle call selection
  const handleToggleSelect = (callId) => {
    setSelectedCalls((prev) => {
      const next = new Set(prev);
      if (next.has(callId)) {
        next.delete(callId);
      } else {
        next.add(callId);
      }
      return next;
    });
  };

  // Select all visible calls
  const handleSelectAll = (checked) => {
    if (checked) {
      const visibleCallIds = filteredCalls.map((c) => c.call_id);
      setSelectedCalls(new Set(visibleCallIds));
    } else {
      setSelectedCalls(new Set());
    }
  };

  // Filter calls based on selected filter
  const filteredCalls = calls.filter((call) => {
    switch (filter) {
      case 'with_suggestions':
        return call.suggestions && call.suggestions.length > 0;
      case 'high_confidence':
        return call.suggestions?.some((s) => s.confidence >= 0.85);
      default:
        return true;
    }
  });

  const allSelected = filteredCalls.length > 0 && filteredCalls.every((c) => selectedCalls.has(c.call_id));

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AutoAwesomeIcon fontSize="large" color="primary" />
            <Typography variant="h5">Batch Ticket Review</Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              type="date"
              size="small"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              sx={{ width: 150 }}
            />
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={(e, v) => v && setFilter(v)}
              size="small"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="with_suggestions">With Suggestions</ToggleButton>
              <ToggleButton value="high_confidence">High Confidence</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="contained"
              onClick={fetchBatchSuggestions}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            >
              {loading ? 'Analyzing...' : 'Analyze Calls'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      {stats.total > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box>
              <Typography variant="h4">{stats.total}</Typography>
              <Typography color="text.secondary">Untagged Calls</Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="info.main">{stats.withSuggestions}</Typography>
              <Typography color="text.secondary">With Suggestions</Typography>
            </Box>
            <Box>
              <Typography variant="h4" color="success.main">{stats.highConfidence}</Typography>
              <Typography color="text.secondary">High Confidence</Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Calls table */}
      {filteredCalls.length > 0 ? (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={selectedCalls.size > 0 && !allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Caller</TableCell>
                  <TableCell>Extension</TableCell>
                  <TableCell>Suggested Ticket</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCalls.map((call) => {
                  const suggestion = call.suggestions?.[0];
                  const isHighConfidence = suggestion?.confidence >= 0.85;
                  const ticketUrl = suggestion ? getTicketUrl(suggestion.ticket_number) : null;

                  return (
                    <TableRow
                      key={call.call_id}
                      selected={selectedCalls.has(call.call_id)}
                      sx={{
                        bgcolor: isHighConfidence ? 'success.50' : 'inherit',
                        '&:hover': { bgcolor: isHighConfidence ? 'success.100' : 'action.hover' },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedCalls.has(call.call_id)}
                          onChange={() => handleToggleSelect(call.call_id)}
                          disabled={!suggestion}
                        />
                      </TableCell>
                      <TableCell>{formatTime(call.started_at)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {call.caller_number}
                        </Typography>
                        {call.caller_name && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {call.caller_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {call.extension}
                          {call.extension_name && ` - ${call.extension_name}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {suggestion ? (
                          <Box>
                            {ticketUrl ? (
                              <Link href={ticketUrl} target="_blank" rel="noopener" sx={{ textDecoration: 'none' }}>
                                <Chip
                                  label={`#${suggestion.ticket_number}`}
                                  size="small"
                                  color={isHighConfidence ? 'success' : 'default'}
                                  variant="outlined"
                                  clickable
                                />
                              </Link>
                            ) : (
                              <Chip
                                label={`#${suggestion.ticket_number}`}
                                size="small"
                                color={isHighConfidence ? 'success' : 'default'}
                                variant="outlined"
                              />
                            )}
                            {suggestion.ticket_summary && (
                              <Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                                {suggestion.ticket_summary}
                              </Typography>
                            )}
                            {suggestion.company && (
                              <Typography variant="caption" color="primary.main" fontWeight="bold">
                                {suggestion.company}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            No suggestions
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {suggestion && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                              variant="body2"
                              color={isHighConfidence ? 'success.main' : 'text.secondary'}
                              fontWeight={isHighConfidence ? 'bold' : 'normal'}
                            >
                              {Math.round(suggestion.confidence * 100)}%
                            </Typography>
                            {isHighConfidence && (
                              <Tooltip title="High confidence">
                                <VerifiedIcon fontSize="small" color="success" />
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {suggestion && (
                          <Chip label={suggestion.source} size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {suggestion && (
                            <Button
                              size="small"
                              variant={isHighConfidence ? 'contained' : 'outlined'}
                              color="success"
                              onClick={() => handleConfirm(call, suggestion)}
                              disabled={confirming[call.call_id]}
                              startIcon={confirming[call.call_id] ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                            >
                              Confirm
                            </Button>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleSkip(call.call_id)}
                            title="Skip"
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Bulk action bar */}
          {selectedCalls.size > 0 && (
            <Paper
              sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px solid',
                borderColor: 'divider',
                zIndex: 1000,
              }}
              elevation={8}
            >
              <Typography>
                {selectedCalls.size} call{selectedCalls.size !== 1 ? 's' : ''} selected
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button onClick={() => setSelectedCalls(new Set())}>Clear Selection</Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleBulkConfirm}
                  startIcon={<CheckCircleIcon />}
                >
                  Confirm All Selected
                </Button>
              </Box>
            </Paper>
          )}
        </>
      ) : loading ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography color="text.secondary">Analyzing calls...</Typography>
        </Paper>
      ) : calls.length === 0 && stats.total === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <AutoAwesomeIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No Calls to Review
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Select a date and click "Analyze Calls" to find untagged calls with suggested tickets.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FilterIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No Calls Match Filter
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Try changing the filter to see more calls.
          </Typography>
        </Paper>
      )}
    </Container>
  );
}
